import { mobileShopApi } from '../mobileShopApi';
import { Role, Screen, ScreenPermission } from '../../utils/auth';

export interface LoginResponse {
  token: string;
  userName: string;
  fullName?: string;
  role: Role;
  roleName?: string;
  permissions: Partial<Record<Screen, ScreenPermission>>;
}

export interface ShopUser {
  id?: number;
  userName: string;
  password?: string;
  fullName?: string;
  role: Role;
  roleName?: string;
}

/** A role the shop created, or one of the three that shipped. */
export interface ShopRole {
  id?: number;
  roleCode?: string;
  roleName: string;
  description?: string;
  systemRole?: boolean;
  /** How many logins use it. Delete is refused while this is not zero. */
  userCount?: number;
}

export interface RolePermissionRow extends ScreenPermission {
  id?: number;
  role: Role;
  screen: Screen;
}

export interface PermissionGrid {
  roles: Role[];
  roleDetails: ShopRole[];
  screens: Screen[];
  permissions: RolePermissionRow[];
}

export const authApi = mobileShopApi.injectEndpoints({
  endpoints: (builder) => ({
    /** Asked before anything else: brand-new shop, or an existing one? */
    getSetupStatus: builder.query<{ setupNeeded: boolean; shopName?: string }, void>({
      query: () => '/auth/status',
      providesTags: ['ShopSetting'],
    }),

    setupShop: builder.mutation<LoginResponse, { shopName: string; fullName: string; userName: string; password: string }>({
      query: (body) => ({ url: '/auth/setup', method: 'POST', body }),
    }),

    login: builder.mutation<LoginResponse, { userName: string; password: string }>({
      query: (body) => ({ url: '/auth/login', method: 'POST', body }),
    }),

    /* owner only */

    getUsers: builder.query<ShopUser[], void>({
      query: () => '/user',
      providesTags: ['User'],
    }),

    saveUser: builder.mutation<ShopUser, ShopUser>({
      query: (body) => ({ url: '/user', method: 'POST', body }),
      invalidatesTags: ['User'],
    }),

    updateUser: builder.mutation<ShopUser, ShopUser>({
      query: (body) => ({ url: '/user', method: 'PUT', body }),
      invalidatesTags: ['User'],
    }),

    deleteUser: builder.mutation<void, number>({
      query: (id) => ({ url: `/user/${id}`, method: 'DELETE' }),
      invalidatesTags: ['User'],
    }),

    /* roles the owner creates */

    getRoles: builder.query<ShopRole[], void>({
      query: () => '/role',
      providesTags: ['Permission'],
    }),

    saveRole: builder.mutation<ShopRole, ShopRole>({
      query: (body) => ({ url: '/role', method: 'POST', body }),
      invalidatesTags: ['Permission'],
    }),

    updateRole: builder.mutation<ShopRole, ShopRole>({
      query: (body) => ({ url: '/role', method: 'PUT', body }),
      invalidatesTags: ['Permission'],
    }),

    deleteRole: builder.mutation<void, number>({
      query: (id) => ({ url: `/role/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Permission', 'User'],
    }),

    getPermissionGrid: builder.query<PermissionGrid, void>({
      query: () => '/role-permission',
      providesTags: ['Permission'],
    }),

    savePermissionGrid: builder.mutation<RolePermissionRow[], RolePermissionRow[]>({
      query: (body) => ({ url: '/role-permission', method: 'PUT', body }),
      invalidatesTags: ['Permission'],
    }),

    runBackup: builder.mutation<{ file: string; sizeKb: number }, void>({
      query: () => ({ url: '/system/backup', method: 'POST' }),
    }),
  }),
});

export const {
  useGetSetupStatusQuery,
  useSetupShopMutation,
  useLoginMutation,
  useGetUsersQuery,
  useSaveUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetRolesQuery,
  useSaveRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionGridQuery,
  useSavePermissionGridMutation,
  useRunBackupMutation,
} = authApi;
